'use strict';

const Measurement = require('bunsen/models/inventory/Measurement');
const displayConstants = require('../../constants/measurementConstants').display;
const formatters = require('../../helpers/measurementFormatters.es');
const measurementConstants = require('../../constants/measurementConstants.es');

const { DEFAULT_FEET_CUTOFF, LOWER_FEET_CUTOFF } = require('dibs-measurement-conversions');

const measurementLabels = measurementConstants.labels;
const schemaTypes = require('../../constants/schemaTypes');

/**
 *
 * @param {object} obj
 * @param {string|number} obj.value The value to use for the dimension
 * @param {string} obj.unit The unit of the dimension
 * @param {string} obj.unitLabel The label for the unit/dimension
 * @returns {boolean}
 */
function filterMeasurements(obj) {
    return typeof obj === 'object'
        && (typeof obj.value === 'string' || typeof obj.value === 'number')
        && !!obj.unit
        && !!obj.unitLabel;
}

function hasCategory(item, categoryLabel) {
    const itemCategories = item.getItemCategories();

    if (itemCategories) {
        const itemCategory = itemCategories.at(0);

        if (itemCategory) {
            const category = itemCategory.getCategory();

            if (category) {
                const url = category.getBrowseUrl();

                if (url && url.indexOf(categoryLabel) > -1) {
                    return true;
                }
            }
        }
    }

    return false;
}

function showFeet(item) {
    return hasCategory(item, 'rug') || hasCategory(item, 'mirror');
}

function getMeasurement(config, key) {
    const contents = [];
    const item = config.item;
    const value = item.get('measurement.' + key);
    const units = item.get('measurement.unit');
    const shouldShowFeet = showFeet(item);
    const isFurniture = item.isFurniture();
    let formattedValue;
    if (value && units) {
        formattedValue = formatters.formatMeasurement(value, units, shouldShowFeet, isFurniture);
    }

    if (formattedValue) {
        contents.push({
            isLink: false,
            value: formattedValue
        });
    }

    return contents;
}

function getMeasurementCustom(config) {
    const {item, valueKey, unitsKey, transform} = config;
    const contents = [];
    const value = item.get('measurement.' + valueKey);
    const units = item.get('measurement.' + unitsKey);
    let formattedValue;

    if (value && units) {
        formattedValue = transform(value, units);
    }

    if (formattedValue) {
        contents.push({
            isLink: false,
            value: formattedValue
        });
    }

    return contents;
}

/**
 * Create dimensions sets in an ordered manner
 * @param {object} config
 * @param {object} dimensions
 * @param {{ value: string, unit: string, unitLabel: string }[]} [dimensions.IN] Array of objects for height, width, depth
 * @param {{ value: string, unit: string, unitLabel: string }[]} [dimensions.CM] Array of objects for height, width, depth
 * @param {{ value: string, unit: string, unitLabel: string }[]} [dimensions.MM] Array of objects for height, width, depth
 * @returns {Array}
 */
function pushDimensionsInOrder(config, dimensions) {
    const {item} = config;
    const {IN, CM, MM} = dimensions;
    const contents = [];

    if (IN && IN.length > 0) {
        contents.push(IN);
    }

    if (CM && CM.length > 0) {
        contents.push(CM);
    }

    if (MM && MM.length > 0) {
        contents.push(MM);
    }

    return contents;
}

function getDisplayValue(shouldShowFeet) {
    const formattersMap = {
        [Measurement.units.IN]: formatters.formatInchesNoFeet.bind(formatters)
    };
    const rugsOrMirrorsFormattersMap = {
        [Measurement.units.IN]: formatters.formatInchesAndFeet.bind(formatters)
    };

    return ({ value, unit }) => {
        let displayValue;

        if (!shouldShowFeet && formattersMap[unit]) {
            displayValue = formattersMap[unit]({ value });
        } else if (shouldShowFeet && rugsOrMirrorsFormattersMap[unit]) {
            displayValue = rugsOrMirrorsFormattersMap[unit]({ value, feetCutOff: LOWER_FEET_CUTOFF });
        }

        return displayValue;
    };
}

function getDimensionsFormatted(config, units) {
    const {item} = config;
    const measurementNames = ['height', 'width', 'depth', 'length'];
    const measurementsMap = {height: false, width: false, depth: false, length: false};
    const unitsReverse = {
        [Measurement.units.CM]: Measurement.units.IN,
        [Measurement.units.IN]: Measurement.units.CM
    };
    const unitsLabels = {
        [Measurement.units.CM]: displayConstants[Measurement.units.CM],
        [Measurement.units.IN]: displayConstants[Measurement.units.IN]
    };
    const unitsLabelsReverse = {
        [Measurement.units.CM]: displayConstants[Measurement.units.IN],
        [Measurement.units.IN]: displayConstants[Measurement.units.CM]
    };
    const convertersMap = {
        [Measurement.units.CM]: formatters.centimetersToInches,
        [Measurement.units.IN]: formatters.inchesToCentimeters
    };
    const dimensions = {};

    const shouldShowFeet = showFeet(item);

    dimensions[units] = measurementNames
        .map(name => {
            const value = item.get('measurement.' + name);
            let measurement;

            if (value) {
                measurementsMap[name] = true;

                measurement = {
                    value: value,
                    unit: unitsLabels[units],
                    unitLabel: measurementLabels[name],
                    schemaType: schemaTypes[name]
                };

                const displayValueCheck = getDisplayValue(shouldShowFeet);
                const displayValue = displayValueCheck({ value, unit: units });
                if (displayValue) {
                    measurement.displayValue = displayValue;
                }
            }

            return measurement;
        })
        .filter(filterMeasurements);

    const unitsReversed = unitsReverse[units];
    dimensions[unitsReversed] = measurementNames
        .map((name) => {
            const value = item.get('measurement.' + name);
            let measurement;

            if (value && measurementsMap[name]) {
                const converterMethod = convertersMap[units];
                const convertedValue = converterMethod({ value, feetCutOff: shouldShowFeet ? LOWER_FEET_CUTOFF : DEFAULT_FEET_CUTOFF });

                measurement = {
                    value: convertedValue,
                    unit: unitsLabelsReverse[units],
                    unitLabel: measurementLabels[name],
                    schemaType: schemaTypes[name]
                };

                const displayValueCheck = getDisplayValue(shouldShowFeet);
                const displayValue = displayValueCheck({ value: convertedValue, unit: unitsReversed });
                if (displayValue) {
                    measurement.displayValue = displayValue;
                }
            }

            return measurement;
        })
        .filter(filterMeasurements);

    return pushDimensionsInOrder(config, dimensions);
}

/**
 *
 * @param config
 * @returns {Array}
 */
function getDimensionsFormattedMm(config) {
    const {item} = config;
    const measurementNames = ['height', 'width', 'depth'];
    const dimensions = {};
    // use this data structure since the `pushDimensionsInOrder` method
    // is expecting an object with keys in `IN`, `CM`, or `MM` format
    dimensions[Measurement.units.MM] = measurementNames.map((name) => {
        const value = item.get('measurement.' + name);
        let measurement;

        if (value) {
            measurement = {
                value: value,
                unit: displayConstants[Measurement.units.MM],
                unitLabel: measurementLabels[name],
                schemaType: schemaTypes[name]
            };
        }

        return measurement;
    })
    .filter(filterMeasurements);

    return pushDimensionsInOrder(config, dimensions);
}

module.exports = {
    getDimensions(config) {
        const { item } = config;
        const units = item.get('measurement.unit');
        let contents;

        switch (units) {
            case Measurement.units.MM:
                contents = getDimensionsFormattedMm(config);
                break;
            case Measurement.units.CM:
            case Measurement.units.IN:
                contents = getDimensionsFormatted(config, units);
                break;
            default:
                contents = [];
                break;
        }

        return contents;
    },

    getDiameter: function (config) {
        return getMeasurement(config, 'diameter');
    },

    getLength(config) {
        return getMeasurement(config, 'length');
    },

    getWeight(config) {
        const inflatedConfig = Object.assign({
            valueKey: 'weight.value',
            unitsKey: 'weight.unit',
            transform: formatters.addUnits
        }, config);
        return getMeasurementCustom(inflatedConfig);
    },

    getSeatHeight(config) {
        return getMeasurement(config, 'secondaryHeight');
    },

    getMarkedSize(config) {
        const inflatedConfig = Object.assign({
            valueKey: 'size.value',
            unitsKey: 'size.standard',
            transform: formatters.addUnitsInParenthesis
        }, config);
        return getMeasurementCustom(inflatedConfig);
    },

    getBust(config) {
        return getMeasurement(config, 'chest');
    },

    getWaist(config) {
        return getMeasurement(config, 'waist');
    },

    getHip(config) {
        return getMeasurement(config, 'hip');
    },

    getShoulderHem(config) {
        return getMeasurement(config, 'shoulderToHem');
    },

    getInseam(config) {
        return getMeasurement(config, 'inseam');
    }
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/details/helpers/detailsMeasurements.es.js